/**
 * dk-bulk-add.js
 * Adds many variants to the cart in one go and refreshes the cart drawer.
 *
 * Used by:
 *   - "Add all variants to cart" on the product page (#10)
 *   - "Add all to cart" on the collection page (#9)
 *   - the per-result "Add to cart" in predictive search
 *
 * Any element with a `data-variant-ids` attribute (comma-separated variant
 * ids) becomes a trigger via event delegation, so it works for markup that is
 * rendered after page load too.
 *
 * All /cart/add.js traffic goes through the shared queue (dk-cart-queue.js),
 * the same gate the CSV importer uses. That queue serializes writes, honors a
 * Cloudflare 1015's Retry-After, and bisects a rejected batch instead of firing
 * one request per variant — so these buttons can no longer trip the rate limit,
 * and any variant that fails is reported instead of silently dropped.
 */
(function () {
  if (window.dkBulkAdd) return;

  function getRoot() {
    return window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
  }

  function unmarkEmpty(drawer) {
    if (drawer && drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
  }

  async function refreshDrawer() {
    var drawer = document.querySelector('cart-drawer');
    try {
      var read = window.dkCartQueue && window.dkCartQueue.read
        ? window.dkCartQueue.read
        : function (url, opts) { return fetch(url, opts).then(function (r) { return r.json().then(function (j) { return { json: j }; }); }); };
      var res = await read(getRoot() + '?sections=cart-drawer,cart-icon-bubble', {
        headers: { Accept: 'application/json' },
      });
      var data = res && res.json ? res.json : res;
      if (drawer && data && typeof drawer.renderContents === 'function') {
        unmarkEmpty(drawer);
        drawer.renderContents({ sections: data });
        return;
      }
    } catch (e) {}
    window.location.reload();
  }

  // A compact, self-contained toast so these buttons — which live on product /
  // collection / search pages, away from the cart drawer's report panel — can
  // still tell the shopper what happened, especially what did NOT get added.
  function showToast(addedCount, failed) {
    var existing = document.getElementById('dk-bulk-toast');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var esc = function (s) {
      return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    };

    var el = document.createElement('div');
    el.id = 'dk-bulk-toast';
    el.setAttribute('role', 'status');
    el.style.cssText =
      'position:fixed;right:16px;bottom:16px;z-index:2147483000;width:min(360px,calc(100vw - 32px));' +
      'background:#fff;color:#121212;border:1px solid rgba(0,0,0,.14);border-radius:12px;' +
      'box-shadow:0 12px 40px rgba(0,0,0,.22);padding:12px 14px;font-size:13px;line-height:1.45;';

    var html = '<div style="display:flex;justify-content:space-between;gap:10px;align-items:start;">';
    html += '<strong>' + (addedCount > 0 ? '&#x2713; ' + addedCount + ' added to cart' : 'Nothing was added') + '</strong>';
    html += '<button type="button" aria-label="Close" style="border:0;background:none;font-size:18px;line-height:1;cursor:pointer;color:inherit;" data-dk-toast-close>&times;</button>';
    html += '</div>';

    if (failed && failed.length) {
      html += '<div style="color:#b00020;margin-top:6px;"><strong>' + failed.length + ' could not be added:</strong></div>';
      html += '<ul style="margin:4px 0 0;padding-left:18px;max-height:140px;overflow:auto;">';
      failed.slice(0, 30).forEach(function (f) {
        var reason = f.reason === 'rate_limited'
          ? 'store was rate-limiting'
          : (f.error || 'rejected by the store');
        html += '<li>Variant ' + esc(f.id) + ' &mdash; ' + esc(reason) + '</li>';
      });
      if (failed.length > 30) html += '<li>…and ' + (failed.length - 30) + ' more</li>';
      html += '</ul>';
    }

    el.innerHTML = html;
    document.body.appendChild(el);

    el.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-dk-toast-close]')) {
        if (el.parentNode) el.parentNode.removeChild(el);
      }
    });

    // Auto-dismiss only on a clean success; keep failures on screen to be read.
    if (!failed || !failed.length) {
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 4000);
    }
  }

  // Fallback used only if the shared queue asset is missing: one atomic request
  // per 50 variants, no per-item explosion, no retry storm.
  async function addWithoutQueue(items) {
    var added = [], failed = [];
    for (var i = 0; i < items.length; i += 50) {
      var chunk = items.slice(i, i + 50);
      try {
        var res = await fetch(getRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: chunk }),
        });
        if (res.ok) {
          added = added.concat(chunk);
        } else {
          var msg = 'HTTP ' + res.status;
          try { var j = await res.json(); msg = j.description || j.message || msg; } catch (e) {}
          chunk.forEach(function (it) { failed.push({ id: it.id, error: msg }); });
        }
      } catch (e) {
        chunk.forEach(function (it) { failed.push({ id: it.id, error: 'Network error' }); });
      }
    }
    return { added: added, failed: failed, rateLimited: false };
  }

  /**
   * @param {Array<number|{id:number,quantity:number}>} variantIds
   * @param {HTMLElement} [btn]
   * @param {{onDone?:function(number, Array):void, silent?:boolean}} [opts]
   */
  window.dkBulkAdd = async function (variantIds, btn, opts) {
    opts = opts || {};

    var items = (variantIds || [])
      .map(function (v) {
        if (v && typeof v === 'object') return { id: Number(v.id), quantity: v.quantity || 1 };
        return { id: Number(v), quantity: 1 };
      })
      .filter(function (it) { return it.id; });

    if (!items.length) return { added: 0, failed: [] };

    var originalLabel = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }

    var result;
    try {
      if (window.dkCartQueue && typeof window.dkCartQueue.addBatch === 'function') {
        result = await window.dkCartQueue.addBatch(items, {
          chunkSize: 50,
          onProgress: function (done, total, added) {
            if (btn) btn.textContent = 'Adding… ' + Math.min(done, total) + '/' + total;
          },
        });
      } else {
        result = await addWithoutQueue(items);
      }
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = originalLabel;
      }
    }

    if (result.added.length > 0) await refreshDrawer();

    if (!opts.silent) showToast(result.added.length, result.failed);
    if (typeof opts.onDone === 'function') opts.onDone(result.added.length, result.failed);
    return { added: result.added.length, failed: result.failed };
  };

  // Delegated trigger for any [data-variant-ids] element.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-variant-ids]') : null;
    if (!btn) return;
    var ids = (btn.getAttribute('data-variant-ids') || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
    if (!ids.length) return;
    e.preventDefault();
    window.dkBulkAdd(ids, btn);
  });
})();
